import React from "react";
import { useGetTncCurrentMsaQuery } from "../store/slices/settingsSlice";
import BillingDetailsIcon from "../../assets/icons/invoice_history.svg2";
import MyIcon from "../Common/MyIcon";

const { countries, defaultCountry } = JSON.parse(
  document.querySelector("#app").dataset.page
).props;

function formatLabel(value) {
  if (!value) return value;
  return value
    .replace(/_/g, " ")
    .replace(/^./, (character) => character.toUpperCase());
}

function formatMoney(value, currency) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return currency ? `${currency} ${value}` : value;

  const formattedAmount = amount.toFixed(4).replace(/(?:\.0+|(?:(\.\d*?[1-9]))0+)$/, "$1");
  const decimalPlaces = formattedAmount.includes(".") ? formattedAmount.split(".")[1].length : 0;
  const displayAmount = `${formattedAmount}${"0".repeat(Math.max(0, 2 - decimalPlaces))}`;

  return currency ? `${currency} ${displayAmount}` : displayAmount;
}

function getCountryName(countryCode) {
  if (countryCode === "--") return defaultCountry?.country_name;
  return countries.find((country) => country.country_code === countryCode)?.country_name || countryCode;
}

function formatMsaChange(change, snapshot) {
  const countryRateMatch = change.field.match(/^subscriptionsData\.(\d+)\.countryRates\.rates\.country:([^.]+)$/);
  if (countryRateMatch) {
    const [, subscriptionIndex, countryIso] = countryRateMatch;
    const countryRate = change.current || change.previous;
    const countryName = countryRate?.country_name || getCountryName(countryIso);
    const service = snapshot?.subscriptionsData?.[subscriptionIndex]?.clientProduct || "service";

    return change.current
      ? `Messaging rates for ${countryName} have been added to ${service}.`
      : `Messaging rates for ${countryName} have been removed from ${service}.`;
  }

  const countryChargeMatch = change.field.match(/^subscriptionsData\.(\d+)\.countryRates\.rates\.country:([^.]+)\.rates\.(\d+)\.charge$/);
  if (countryChargeMatch) {
    const [, subscriptionIndex, countryIso, rateIndex] = countryChargeMatch;
    const country = snapshot?.subscriptionsData?.[subscriptionIndex]?.countryRates?.rates
      ?.find((rate) => rate.country_iso === countryIso);
    const rate = country?.rates?.[rateIndex];
    const countryName = country?.country_name || getCountryName(countryIso);
    const service = formatLabel(rate?.for || rate?.product || "Sms");
    const currency = snapshot?.subscriptionsData?.[subscriptionIndex]?.billingCurrency;

    return `${service} messaging rate for ${countryName} has changed from ${formatMoney(change.previous, currency)} to ${formatMoney(change.current, currency)}.`;
  }

  const chargeMatch = change.field.match(/^subscriptionsData\.(\d+)\.serviceCharges\.(\d+)(?:\.(product|cost))?$/);
  if (chargeMatch) {
    const [, subscriptionIndex, chargeIndex, property] = chargeMatch;
    const currency = snapshot?.subscriptionsData?.[subscriptionIndex]?.billingCurrency;

    if (!property) {
      const charge = change.current || change.previous || {};
      const description = charge.product || "service charge";
      return change.current
        ? `A service charge for ${description} has been added.`
        : `The service charge for ${description} has been removed.`;
    }

    if (property === "cost") {
      const description = snapshot?.subscriptionsData?.[subscriptionIndex]?.serviceCharges?.[chargeIndex]?.product || "service";
      return `The service charge for ${description} has changed from ${formatMoney(change.previous, currency)} to ${formatMoney(change.current, currency)}.`;
    }

    return `A service charge has changed from ${change.previous} to ${change.current}.`;
  }

  if (/^subscriptionsData\.\d+\.supportPackage$/.test(change.field)) {
    return `Support package has changed from ${change.previous || "none"} to ${change.current || "none"}.`;
  }

  if (/^subscriptionsData\.\d+\.additionalFeatures\.\d+$/.test(change.field)) {
    if (change.current && !change.previous) return `Additional feature ${change.current} has been added.`;
    if (change.previous && !change.current) return `Additional feature ${change.previous} has been removed.`;
    return `Additional feature has changed from ${change.previous} to ${change.current}.`;
  }

  const match = change.field.match(/^subscriptionsData\.(\d+)\.countryRates\.rates\.(\d+)\.rates\.(\d+)\.charge$/);
  if (match) {
    const [, subscriptionIndex, countryIndex, rateIndex] = match;
    const country = snapshot?.subscriptionsData?.[subscriptionIndex]?.countryRates?.rates?.[countryIndex];
    const rate = country?.rates?.[rateIndex];
    const countryName = country?.country_name || getCountryName(country?.country_iso);
    const service = formatLabel(rate?.for || rate?.product || "Sms");
    const currency = snapshot?.subscriptionsData?.[subscriptionIndex]?.billingCurrency;

    return `${service} country rate for ${countryName} has been updated from ${formatMoney(change.previous, currency)} to ${formatMoney(change.current, currency)}.`;
  }

  return `${formatLabel(change.field)} has been updated from ${String(change.previous ?? "—")} to ${String(change.current ?? "—")}.`;
}

function formatDisplayValue(value) {
  if (value === null || value === undefined || value === "") return null;
  if (React.isValidElement(value)) return value;
  if (Array.isArray(value)) {
    const values = value.map(formatDisplayValue).filter(Boolean);
    return values.length ? values.join(", ") : null;
  }
  if (typeof value === "object") {
    const values = Object.values(value).map(formatDisplayValue).filter(Boolean);
    return values.length ? values.join(", ") : null;
  }
  return value;
}

function Row({ label, value }) {
  const displayValue = formatDisplayValue(value);
  if (displayValue === null || displayValue === undefined || displayValue === "") return null;
  return (
    <tr>
      <td className="fw-semibold text-nowrap pe-3 py-1 text-secondary">{label}</td>
      <td className="py-1" style={{ wordBreak: "break-word" }}>
        {displayValue}
      </td>
    </tr>
  );
}

function SectionHeader({ children }) {
  return (
    <tr>
      <td colSpan={2}>
        <hr className="my-2 text-secondary" />
        <span className="fw-bold small text-secondary">{children}</span>
      </td>
    </tr>
  );
}

function commaSeparated(value) {
  return formatDisplayValue(value);
}

function objectValues(value) {
  if (!value || typeof value !== "object") return [];
  return Object.values(value);
}

function formatServiceCharges(serviceCharges) {
  return objectValues(serviceCharges)
    .map((charge) => `${charge.product}: ${charge.cost}`)
    .join("\n");
}

function formatCountryRates(countryRates, currency) {
  if (Array.isArray(countryRates?.rates)) {
    if (countryRates.rates.every((country) => Array.isArray(country?.rates))) {
      return countryRates.rates
        .map((country) => {
          const countryName = country.country_name || getCountryName(country.country_iso);
          const rates = country.rates
            .map((rate) => `${formatLabel(rate.for || rate.product)}: ${formatMoney(rate.charge, currency)}`)
            .join(", ");

          return rates ? `${countryName}: ${rates}` : countryName;
        })
        .join("\n");
    }

    return countryRates.rates
      .map((rate) => {
        const countryCode = rate.country_iso || rate.iso_code || rate.country_name;
        const country = getCountryName(countryCode);
        const defaultCountrySuffix = countryCode === "--" ? " (Default country)" : "";
        const product = formatLabel(rate.for || rate.product);
        const charge = formatMoney(rate.charge, currency);

        return country ? `${product} - ${country}${defaultCountrySuffix}: ${charge}` : `${product}: ${charge}`;
      })
      .join("\n");
  }

  return objectValues(countryRates)
    .map((rate) => {
      const country = rate.country_name || rate.iso_code;
      const rates = Object.entries(rate)
        .filter(([key]) => !["country_name", "iso_code"].includes(key))
        .map(([key, value]) => `${key}: ${value}`)
        .join(", ");

      return rates ? `${country}: ${rates}` : country;
    })
    .join("\n");
}

function PreformattedRow({ label, value }) {
  if (!value) return null;
  return (
    <tr>
      <td className="fw-semibold text-nowrap pe-3 py-1 text-secondary align-top">
        {label}
      </td>
      <td className="py-1">
        <pre
          className="mb-0 small"
          style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {value}
        </pre>
      </td>
    </tr>
  );
}

export default function AccountTNC() {
  const { data, isLoading, isError } = useGetTncCurrentMsaQuery();

  return (
    <div className="bg-purple-gradient-darker-reverse rounded-4 p-3">
      <h5 className="d-flex align-items-center mb-3">
        <MyIcon Icon={BillingDetailsIcon} className="me-2" />
        <span className="ms-2">
          <span className="fw-bold me-2">Customer Service Information</span>
          <div className="small text-secondary">
            Service Information and Terms of Service
          </div>
        </span>
      </h5>
      <hr className="my-3 text-secondary" />

      {isLoading && (
        <p className="text-secondary small mb-0" role="status">
          <i className="fa fa-spinner fa-spin me-2" aria-hidden="true" />
          Generating your MSA document…
        </p>
      )}
      {isError && <p className="text-danger small">Failed to load MSA data.</p>}

      {data && (
        <>
          {data.link && (
            <div className="mb-3">
              <a
                href={data.link}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-colored-reverse rounded-pill fw-semibold"
              >
                View MSA document
              </a>
            </div>
          )}

          {data.pending_acceptance && (
            <div className="alert alert-warning small">
              This MSA is awaiting acceptance by an account controller. Service access remains restricted until it is accepted.
            </div>
          )}

          {!data.pending_acceptance && data.accepted_at && (
            <p className="small text-secondary">
              Accepted on {data.accepted_at}{data.accepted_by?.name ? ` by ${data.accepted_by.name}` : ""}.
            </p>
          )}

          {Array.isArray(data.changes) && data.changes.length > 0 && (
            <div className="mb-3 small">
              <strong>Changes in this version</strong>
              <ul className="mb-0 ps-3">
                {data.changes.map((change) => (
                  <li key={change.field}>{formatMsaChange(change, data.data)}</li>
                ))}
              </ul>
            </div>
          )}

          {data.data && (
            <div className="table-responsive">
              <table className="table table-sm table-borderless mb-0 small">
                <tbody>
                  <Row label="Service name"        value={commaSeparated(data.data.serviceName)} />

                  {Array.isArray(data.data.subscriptionsData) &&
                    data.data.subscriptionsData.map((subscription) => (
                      <React.Fragment key={`${subscription.clientProduct}-${subscription.clientName}`}>
                        <SectionHeader>
                          {subscription.clientProduct} - {subscription.clientName}
                        </SectionHeader>
                        <Row label="Service status"      value={subscription.serviceStatus} />
                        <Row label="Support package"     value={subscription.supportPackage} />
                        <Row label="Subscription start"  value={subscription.subscriptionStartDate} />
                        <Row label="Subscription end"    value={subscription.subscriptionEndDate} />
                        <Row label="Billing currency"    value={subscription.billingCurrency} />
                        <Row label="Authorised users"    value={subscription.authorisedUsers} />
                        <Row label="Communication allowance" value={subscription.inclusiveCommunicationAllowance} />
                        <Row label="Contract notice period"  value={subscription.contractNoticePeriod} />
                        <Row label="Payment notice period"   value={subscription.paymentNoticePeriod} />
                        <Row label="Service restrictions"    value={subscription.serviceRestrictions} />
                        <Row label="Additional features"     value={commaSeparated(subscription.additionalFeatures)} />
                        <PreformattedRow label="Service charges" value={formatServiceCharges(subscription.serviceCharges)} />
                        <PreformattedRow label="Country rates" value={formatCountryRates(subscription.countryRates, subscription.billingCurrency)} />
                      </React.Fragment>
                    ))}

                  {data.data.customer && (
                    <>
                      <SectionHeader>Customer</SectionHeader>
                      <Row label="Name"     value={data.data.customer.name} />
                      <Row label="Status"   value={data.data.customer.status} />
                      <Row label="MSA version" value={data.msa_version} />
                      <Row label="Reference"   value={data.data.customer.reference} />
                    </>
                  )}

                  {Array.isArray(data.data.customer?.clients) &&
                    data.data.customer.clients.length > 0 && (
                      <>
                        <SectionHeader>Products</SectionHeader>
                        {data.data.customer.clients.map((client) => (
                          <tr key={client.id}>
                            <td className="fw-semibold text-nowrap pe-3 py-1 text-secondary">
                              {client.product_type}
                            </td>
                            <td className="py-1">
                              {client.client_name}
                              <span className="ms-2 badge bg-secondary small">
                                {client.active === "Y" ? "Active" : "Inactive"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </>
                    )}
                </tbody>
              </table>
            </div>
          )}

          {Array.isArray(data.history) && data.history.length > 0 && (
            <div className="mt-3 small">
              <strong>MSA history</strong>
              <div className="table-responsive">
                <table className="table table-sm table-striped align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Version</th>
                      <th>Change description</th>
                      <th>Updated</th>
                      <th>Accepted at</th>
                      <th>Status/Accepted by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.history.map((version) => (
                      <tr key={version.version}>
                        <td><a href={version.download_url} target="_blank" rel="noopener noreferrer">Version {version.version}</a></td>
                        <td>
                          {version.custom_message && <p className="mb-2" style={{ whiteSpace: "pre-wrap" }}>{version.custom_message}</p>}
                          {version.changes?.length ? (
                            <ul className="mb-0 ps-3">
                              {version.changes.map((change) => <li key={change.field}>{formatMsaChange(change, version.snapshot)}</li>)}
                            </ul>
                          ) : "Initial MSA version."}
                        </td>
                        <td>{version.generated_at || "—"}</td>
                        <td>{version.accepted_at || "—"}</td>
                        <td>{version.pending_acceptance ? "Awaiting acceptance" : (version.accepted_by || "Accepted")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
